'use client';

import Logo from "@/components/logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';
import Link from "next/link";
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';

const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

export default function SignInClient() {
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [step, setStep] = useState('email');
    const [message, setMessage] = useState('');
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const turnstileRef = useRef<TurnstileInstance>(null);

    const resetCaptcha = () => {
        setCaptchaToken(null);
        turnstileRef.current?.reset();
    };

    const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!captchaToken) {
            setMessage('Please complete the captcha.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, captchaToken }),
            });
            if (response.ok) {
                setStep('code');
                setMessage('Code sent to your email. Please check your inbox. Check your spam folder if you don\'t see it.');
                resetCaptcha();
            } else {
                const data = await response.json().catch(() => null);
                setMessage(data?.message ?? 'Error sending code. Please try again.');
                resetCaptcha();
            }
        } catch (error) {
            console.error('Error:', error);
            setMessage('An unexpected error occurred.');
            resetCaptcha();
        } finally {
            setIsLoading(false);
        }
    };

    const handleCodeSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const response = await fetch('/api/auth/confirm', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code }),
            });
            if (response.ok) {
                router.push('/dashboard');
            } else {
                setMessage('Invalid code. Please try again.');
            }
        } catch (error) {
            console.error('Error:', error);
            setMessage('An unexpected error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    const resendCode = async () => {
        if (!captchaToken) {
            setMessage('Please complete the captcha.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, captchaToken }),
            });
            if (response.ok) {
                setMessage('Code resent to your email.');
                resetCaptcha();
            } else {
                const data = await response.json().catch(() => null);
                setMessage(data?.message ?? 'Error resending code. Please try again.');
                resetCaptcha();
            }
        } catch (error) {
            console.error('Error:', error);
            setMessage('An unexpected error occurred.');
            resetCaptcha();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-12">
            <div
                aria-hidden="true"
                className="halftone pointer-events-none absolute inset-x-0 top-0 h-[26rem] text-primary/25 [mask-image:radial-gradient(46rem_26rem_at_50%_-8rem,black,transparent_72%)]"
            />

            <div className="relative w-full max-w-[420px]">
                <Link
                    href="/"
                    className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to home
                </Link>

                <div className="rounded-3xl border border-border bg-card p-8 shadow-xl shadow-primary/5 sm:p-10">
                    <div className="text-center">
                        <Link href="/" aria-label="Readry home">
                            <Logo className="text-4xl" />
                        </Link>
                        <h1 className="mt-5 font-display text-2xl font-semibold text-foreground">
                            {step === 'email' ? 'Sign in or create an account' : 'Check your email'}
                        </h1>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                            {step === 'email'
                                ? 'No password needed — we’ll email you a sign-in code. New here? The same form creates your free account.'
                                : `We sent a code to ${email}.`}
                        </p>
                    </div>

                    {message && (
                        <p className="mt-5 rounded-xl bg-secondary px-4 py-3 text-center text-sm text-secondary-foreground">
                            {message}
                        </p>
                    )}

                    {step === 'email' ? (
                        <form onSubmit={handleEmailSubmit} className="mt-6 space-y-4">
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                autoComplete="email"
                                className="h-11 rounded-xl"
                            />
                            {turnstileSiteKey ? (
                                // grayscale: the widget is an iframe we can't retheme,
                                // but a parent filter renders it in e-ink monochrome
                                <div className="flex justify-center grayscale">
                                    <Turnstile
                                        ref={turnstileRef}
                                        siteKey={turnstileSiteKey}
                                        options={{ theme: 'light' }}
                                        onSuccess={setCaptchaToken}
                                        onExpire={() => setCaptchaToken(null)}
                                        onError={() => setCaptchaToken(null)}
                                    />
                                </div>
                            ) : (
                                <p className="text-center text-sm text-destructive">Captcha is not configured.</p>
                            )}
                            <Button
                                type="submit"
                                className="h-11 w-full rounded-full text-base font-semibold"
                                disabled={isLoading || !turnstileSiteKey || !captchaToken}
                            >
                                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Send code
                            </Button>
                            <p className="text-center text-xs text-muted-foreground">
                                Free forever for newsletters. No credit card required.
                            </p>
                        </form>
                    ) : (
                        <div className="mt-6 space-y-4">
                            <form onSubmit={handleCodeSubmit} className="space-y-4">
                                <Input
                                    type="text"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    placeholder="Enter the code from your email"
                                    required
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    className="h-11 rounded-xl text-center tracking-widest"
                                />
                                <Button type="submit" className="h-11 w-full rounded-full text-base font-semibold" disabled={isLoading}>
                                    {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                    Verify code
                                </Button>
                            </form>
                            {turnstileSiteKey ? (
                                <div className="flex w-full justify-center grayscale">
                                    <Turnstile
                                        ref={turnstileRef}
                                        siteKey={turnstileSiteKey}
                                        options={{ theme: 'light' }}
                                        onSuccess={setCaptchaToken}
                                        onExpire={() => setCaptchaToken(null)}
                                        onError={() => setCaptchaToken(null)}
                                    />
                                </div>
                            ) : null}
                            <Button
                                variant="outline"
                                onClick={resendCode}
                                className="h-11 w-full rounded-full"
                                disabled={isLoading || !turnstileSiteKey || !captchaToken}
                            >
                                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Resend code
                            </Button>
                        </div>
                    )}
                </div>

                <p className="mt-6 text-center text-xs text-muted-foreground">
                    By continuing you agree to our{' '}
                    <Link href="/terms" className="underline underline-offset-4 hover:text-foreground">
                        Terms
                    </Link>{' '}
                    and{' '}
                    <Link href="/privacy-policy" className="underline underline-offset-4 hover:text-foreground">
                        Privacy Policy
                    </Link>
                    .
                </p>
            </div>
        </div>
    );
}
